/**
 * Transition Process Generator Component
 * 
 * UI for testing and managing automatic process generation for transitions
 */

import React, { useState, useEffect } from 'react';
import { Card, List, Button, message, Tag, Alert, Spin } from 'antd';
import { ThunderboltOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';

interface TransitionWithoutProcess {
  transition_id: string;
  transition_name: string;
  artifact_id: string;
  artifact_name: string;
  transition_role: string;
  suggested_process_name: string;
}

export const TransitionProcessGenerator: React.FC<{ artifactId?: string }> = ({ artifactId }) => {
  const [transitions, setTransitions] = useState<TransitionWithoutProcess[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadTransitions();
  }, [artifactId]);

  const loadTransitions = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/prime-transitions/without-process');
      let data = response.data;
      
      // Filter by artifact if specified
      if (artifactId) {
        data = data.filter((t: TransitionWithoutProcess) => t.artifact_id === artifactId);
      }
      
      setTransitions(data);
    } catch (error: any) {
      message.error(`Failed to load transitions: ${error.message}`);
      console.error('Error loading transitions:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateProcess = async (transitionId: string) => {
    try {
      setGenerating(prev => new Set(prev).add(transitionId));
      const response = await apiClient.post(`/prime-transitions/${transitionId}/generate-process`, {});
      message.success(response.data.message);
      
      // Reload transitions
      await loadTransitions();
    } catch (error: any) {
      message.error(`Failed to generate process: ${error.response?.data?.detail || error.message}`);
      console.error('Error generating process:', error);
    } finally {
      setGenerating(prev => {
        const next = new Set(prev);
        next.delete(transitionId);
        return next;
      });
    }
  };

  const generateAllForArtifact = async (artifactIdToGenerate: string) => {
    try {
      setLoading(true);
      const response = await apiClient.post('/prime-transitions/bulk-generate-processes', {
        artifact_id: artifactIdToGenerate
      });
      
      message.success(`Generated ${response.data.success_count} processes`);
      
      // Show messages
      response.data.messages.forEach((msg: string) => {
        console.log(msg);
      });
      
      // Reload transitions
      await loadTransitions();
    } catch (error: any) {
      message.error(`Failed to bulk generate: ${error.message}`);
      console.error('Error bulk generating:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'PROCESSITEM':
        return 'blue';
      case 'MATERIAL':
        return 'green';
      case 'RESOURCE':
        return 'gold';
      case 'CREATION':
        return 'purple';
      default:
        return 'default';
    }
  };

  if (loading && transitions.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
          <div style={{ marginTop: 16 }}>Loading transitions...</div>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ThunderboltOutlined />
            <span>Transition Process Generator</span>
            <Tag color="orange">{transitions.length} transitions need processes</Tag>
          </div>
        }
      >
        {transitions.length === 0 ? (
          <Alert
            message="All transitions have processes"
            description="No transitions found that need process generation."
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
          />
        ) : (
          <>
            <Alert
              message="Auto-Generate Processes"
              description="These transitions don't have processes assigned. Click generate to create processes automatically using the naming convention: {Artifact Name} - {Transition Name}"
              type="info"
              style={{ marginBottom: 16 }}
            />
            
            <List
              dataSource={transitions}
              renderItem={(transition) => (
                <List.Item
                  key={transition.transition_id}
                  actions={[
                    <Button
                      key="generate"
                      type="primary"
                      size="small"
                      icon={<ThunderboltOutlined />}
                      loading={generating.has(transition.transition_id)}
                      onClick={() => generateProcess(transition.transition_id)}
                    >
                      Generate
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{transition.artifact_name}</span>
                        <span style={{ color: '#999' }}>→</span>
                        <span>{transition.transition_name}</span>
                        <Tag color={getRoleColor(transition.transition_role)}>
                          {transition.transition_role}
                        </Tag>
                      </div>
                    }
                    description={
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <WarningOutlined style={{ color: '#faad14' }} />
                        <span>Will create process: <strong>{transition.suggested_process_name}</strong></span>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
            
            {artifactId && transitions.length > 0 && (
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Button
                  type="primary"
                  size="large"
                  icon={<ThunderboltOutlined />}
                  onClick={() => generateAllForArtifact(artifactId)}
                  loading={loading}
                >
                  Generate All Processes for This Artifact
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

